import { Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import lucasPhoto from '@/assets/lucas-santos.jpg';
import mollyPhoto from '@/assets/molly-shelestak.jpg';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { sectionCls } from '@/pages/launch/helpers';

interface LaunchFoundersSectionProps {
  sectionRef: React.Ref<HTMLDivElement>;
  visible: boolean;
}

const LaunchFoundersSection = ({ sectionRef, visible }: LaunchFoundersSectionProps) => (
  <section ref={sectionRef} className={`py-12 sm:py-20 px-4 sm:px-6 ${sectionCls(visible)}`}>
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-10">
        <Badge variant="outline" className="mb-4">
          Built by Operators
        </Badge>
        <h2 className="text-3xl sm:text-4xl font-display font-bold">
          We Built This Because <span className="gradient-text">We Lived It.</span>
        </h2>
        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
          A decade of Monday morning spreadsheets, bottle counts at 3am, and promoter texts 20
          minutes before doors. We know the chaos because we ran in it.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <Card className="p-6 border-border/60">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={lucasPhoto}
              alt="Lucas Santos"
              width={64}
              height={64}
              className="w-16 h-16 rounded-xl object-cover object-top"
            />
            <div>
              <p className="font-semibold">Lucas Santos</p>
              <p className="text-sm text-primary font-medium">Co-Founder & CEO</p>
            </div>
          </div>
          <blockquote className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3 mb-4">
            "I spent ten years running nightclubs in San Francisco. Grew Temple from $2.5M to $9M.
            Built the same spreadsheets by hand at every venue."
          </blockquote>
          <div className="flex flex-wrap gap-2 text-[11px]">
            {[
              '10+ yrs nightclub ops',
              'Temple SF \u2014 $9M peak',
              'Soma Garden \u2014 $60K/night',
              'L+P \u2014 600% revenue growth',
            ].map((t) => (
              <span key={t} className="px-2 py-1 rounded-full bg-muted text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        </Card>
        <Card className="p-6 border-border/60">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={mollyPhoto}
              alt="Molly Shelestak"
              width={64}
              height={64}
              className="w-16 h-16 rounded-xl object-cover object-top"
            />
            <div>
              <p className="font-semibold">Molly Shelestak</p>
              <p className="text-sm text-primary font-medium">Co-Founder & CPO</p>
            </div>
          </div>
          <blockquote className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3 mb-4">
            "20 years making messy data useful. Building the systems that take disconnected tools
            and make them feel like one."
          </blockquote>
          <div className="flex flex-wrap gap-2 text-[11px]">
            {[
              '20+ yrs data platforms',
              'Google (DoubleClick)',
              'Heap \u2014 $2M cost reduction',
              'Contentsquare \u2014 Sr. Dir. PM',
            ].map((t) => (
              <span key={t} className="px-2 py-1 rounded-full bg-muted text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        </Card>
      </div>

      <div className="text-center mt-8">
        <Button size="lg" asChild className="h-12 px-8">
          <Link to="/design-partners">
            <Calendar size={16} className="mr-2" />
            Become a Founding Partner
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

export default LaunchFoundersSection;
